import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthProvider";
import { call } from "../../services/callables";
import { loadPendingUpi, clearPendingUpi, upiModalWatching } from "../../services/upiIntent";

/**
 * Finishes a mobile UPI Intent payment when the tab was reloaded while the customer was in the
 * UPI app (the AddMoneyModal that started it is gone). On success it lands on the same URL as
 * PayU's surl redirect (/checkout?payu=success&txnid=…) so checkout auto-places the order as before.
 */
export const PendingUpiWatcher: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const busy = useRef(false);

  useEffect(() => {
    if (!user?.uid) return;
    const tick = async () => {
      if (busy.current || document.visibilityState !== "visible") return;
      const p = loadPendingUpi();
      if (!p || upiModalWatching.txnid === p.txnid) return;
      busy.current = true;
      try {
        const r: any = await call("payuIntentStatus", { txnid: p.txnid });
        const st = String(r?.status || "");
        if (st === "credited") {
          clearPendingUpi(p.txnid);
          navigate(`${p.path}?payu=success&txnid=${encodeURIComponent(p.txnid)}`);
        } else if (["failed", "expired", "mismatch"].includes(st)) {
          clearPendingUpi(p.txnid);
          toast.error("Your UPI payment was not completed. No money was added.");
        }
      } catch (e: any) {
        if (e?.code === "functions/permission-denied" || e?.code === "functions/not-found") clearPendingUpi(p.txnid);
      } finally {
        busy.current = false;
      }
    };
    tick();
    const t = window.setInterval(tick, 5000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user?.uid, navigate]);

  return null;
};
